import { Button, Descriptions, Divider, Space, Typography, Spin, Modal } from 'antd';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import IndividualTraderService from '../../services/IndividualTraderService';
import { useAuth } from '../../hooks/useAuth';
import { getFileIconByURL } from '../../util/Common';
import { ArrowLeftOutlined } from '@ant-design/icons';
import WorkflowAccModificationService from '../../services/WorkflowAccModificationService';
import dayjs from 'dayjs';

const { Text, Title } = Typography;

export default function IndividualTraderDetail() {
  const { traderId } = useParams();
  const { showNotification } = useAuth();
  const [traderInformation, setTraderInformation] = useState();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const showModal = () => {
    setOpen(true);
  };

  const hideModal = () => {
    setOpen(false);
  };

  const formatValue = value => {
    // Handle boolean
    if (value === true) return 'YES';
    if (value === false) return 'NO';

    // Handle numbers (Potential Epoch Timestamps)
    if (typeof value === 'number') {
      const absValue = Math.abs(value);
      const len = absValue.toString().length;

      // 1. Check for Seconds (usually 9-10 digits)
      // Range: approx years 1900 to 2100
      if (len >= 9 && len <= 10) {
        const date = dayjs.unix(value);
        if (date.isValid()) return date.format('DD-MMMM-YYYY');
      }

      // 2. Check for Milliseconds (usually 12-13 digits)
      if (len >= 12 && len <= 13) {
        const date = dayjs(value);
        if (date.isValid()) return date.format('DD-MMMM-YYYY');
      }
    }

    return value;
  };

  const hanldeModifyTrader = async tradingAccountNo => {
    setConfirmLoading(true);
    const response = await WorkflowAccModificationService.prepareIndividualAcc({
      tradingAccountNo: tradingAccountNo,
    });
    setOpen(false);
    setConfirmLoading(false);
    if (response.success) {
      showNotification('success', 'Done! Successfully modified user status.');
      console.log(response);
      // console.log(response.workFlowId);
      navigate('/modification/individual/' + response.data.workFlowId);
    } else {
      showNotification('error', response.message);
    }
    console.log(response);
  };

  // Get individual trader with id
  const fetchData = async () => {
    // setLoading(true);
    try {
      const result = await IndividualTraderService.getIndividualTrader({
        traderId: traderId,
        // checkerId: user.userId,
      });
      console.log(result);
      // renderData(result.data);
      setTraderInformation(result.data);
    } catch (error) {
      showNotification('error', error.message);
    }
    // setLoading(false);
  };

  const covertCases = text => {
    const result = text.replace(/([A-Z])/g, ' $1');
    const finalResult = result.charAt(0).toUpperCase() + result.slice(1);
    return finalResult;
  };

  useEffect(() => {
    fetchData();
  }, []);

  const goBack = () => {
    navigate(-1); // Go back one page in history
  };

  if (!traderInformation)
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          textAlign: 'center',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Spin size="large" />
      </div>
    );

  return (
    <section>
      <Descriptions
        title={
          <Title level={5} style={{ marginTop: 0 }}>
            Application Information
          </Title>
        }
        size={'middle'}
        column={{ xl: 2, lg: 2, md: 2, sm: 1, xs: 1 }}
      >
        {Object.keys(traderInformation.applicationInfo).map(item => (
          <Descriptions.Item key={item} label={covertCases(item)}>
            <Text strong>{formatValue(traderInformation.applicationInfo[item])}</Text>
          </Descriptions.Item>
        ))}
      </Descriptions>
      <Divider />

      <Descriptions
        title={
          <Title level={5} style={{ marginTop: 0 }}>
            Individual Trader
          </Title>
        }
        size={'middle'}
        column={{ xl: 2, lg: 2, md: 2, sm: 1, xs: 1 }}
      >
        {Object.keys(traderInformation.individualTrader).map(item => (
          <Descriptions.Item key={item} label={covertCases(item)}>
            <Text strong>{formatValue(traderInformation.individualTrader[item])}</Text>
          </Descriptions.Item>
        ))}
      </Descriptions>
      <Divider />
      <Descriptions
        title={
          <Title level={5} style={{ marginTop: 0 }}>
            Email
          </Title>
        }
        size={'middle'}
        column={{ xl: 2, lg: 2, md: 2, sm: 1, xs: 1 }}
      >
        <Descriptions.Item label={'CC Email'}>
          <Text strong>{traderInformation.ccEmail}</Text>
        </Descriptions.Item>
        <Descriptions.Item label={'BCC Email'}>
          <Text strong>{traderInformation.bccEmail}</Text>
        </Descriptions.Item>
      </Descriptions>
      <Divider />
      <Descriptions
        title={
          <Title level={5} style={{ marginTop: 0 }}>
            Trader Profile
          </Title>
        }
        size={'middle'}
        column={{ xl: 2, lg: 2, md: 2, sm: 1, xs: 1 }}
      >
        {Object.keys(traderInformation.individualProfile).map(item => (
          <Descriptions.Item key={item} label={covertCases(item)}>
            <Text strong>{formatValue(traderInformation.individualProfile[item])}</Text>
          </Descriptions.Item>
        ))}
      </Descriptions>
      <Divider />

      <Descriptions
        title={
          <Title level={5} style={{ marginTop: 0 }}>
            Trading Bank Account
          </Title>
        }
        size={'middle'}
        column={{ xl: 2, lg: 2, md: 2, sm: 1, xs: 1 }}
      >
        {Object.keys(traderInformation.tradingBankAccount).map(item => (
          <Descriptions.Item key={item} label={covertCases(item)}>
            <Text strong>{formatValue(traderInformation.tradingBankAccount[item])}</Text>
          </Descriptions.Item>
        ))}
      </Descriptions>
      <Divider />

      <Descriptions
        title={
          <Title level={5} style={{ marginTop: 0 }}>
            Personal Bank Account
          </Title>
        }
        size={'middle'}
        column={{ xl: 2, lg: 2, md: 2, sm: 1, xs: 1 }}
      >
        {Object.keys(traderInformation.personalBankAccount).map(item => (
          <Descriptions.Item key={item} label={covertCases(item)}>
            <Text strong>{formatValue(traderInformation.personalBankAccount[item])}</Text>
          </Descriptions.Item>
        ))}
      </Descriptions>
      <Divider />

      <Descriptions
        title={
          <Title level={5} style={{ marginTop: 0 }}>
            Communication Address
          </Title>
        }
        size={'middle'}
        column={{ xl: 3, lg: 2, md: 2, sm: 1, xs: 1 }}
      >
        {Object.keys(traderInformation.communicationAddress).map(item => (
          <Descriptions.Item key={item} label={covertCases(item)}>
            <Text strong>{formatValue(traderInformation.communicationAddress[item])}</Text>
          </Descriptions.Item>
        ))}
      </Descriptions>
      <Divider />

      <Descriptions
        title={
          <Title level={5} style={{ marginTop: 0 }}>
            Permanent Address
          </Title>
        }
        size={'middle'}
        column={{ xl: 3, lg: 2, md: 2, sm: 1, xs: 1 }}
      >
        {Object.keys(traderInformation.permanentAddress).map(item => (
          <Descriptions.Item key={item} label={covertCases(item)}>
            <Text strong>{formatValue(traderInformation.permanentAddress[item])}</Text>
          </Descriptions.Item>
        ))}
      </Descriptions>
      <Divider />

      <Descriptions
        title={
          <Title level={5} style={{ marginTop: 0 }}>
            Individual Proof Document
          </Title>
        }
        size={'middle'}
        column={{ xl: 2, lg: 2, md: 2, sm: 1, xs: 1 }}
      >
        {Object.keys(traderInformation.individualProofDocument).map(item => (
          <Descriptions.Item key={item} label={covertCases(item).replace(/ Key$/, '')}>
            <a
              href={traderInformation.individualProofDocument[item]}
              target="_blank"
              rel="noopener noreferrer"
            >
              {getFileIconByURL(traderInformation.individualProofDocument[item])}{' '}
              {covertCases(item).replace(/ Key$/, '')}
            </a>
          </Descriptions.Item>
        ))}
      </Descriptions>
      <Divider />
      <Space
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItem: 'center',
          marginTop: '2rem',
        }}
      >
        <Button type="default" size={'large'} onClick={goBack}>
          <ArrowLeftOutlined /> Back
        </Button>
        <Button type="primary" danger size={'large'} onClick={showModal}>
          Modify
        </Button>
      </Space>
      {open && (
        <Modal
          title={traderInformation.individualTrader.tradingAccountNo}
          open={open}
          onOk={() => hanldeModifyTrader(traderInformation.individualTrader.tradingAccountNo)}
          onCancel={hideModal}
          okText="OK"
          cancelText="Cancel"
          confirmLoading={confirmLoading}
        >
          Are you sure to modify this trader?
        </Modal>
      )}
    </section>
  );
}
